import { useEffect, useRef } from "react";

/**
 * Runs `fn` now (even in a background tab, so an opened view never sits on its
 * skeleton) and then every `ms` while `enabled` and the page is visible. Hidden
 * pages do no repeat work; coming back (visibilitychange / pageshow) refreshes
 * at once. Never overlaps two calls.
 */
export function usePollWhileVisible(fn: () => Promise<void> | void, ms: number, enabled: boolean): void {
  const latest = useRef(fn);
  useEffect(() => { latest.current = fn; });
  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    let inFlight = false;
    let stopped = false;
    const visible = () => document.visibilityState === "visible";
    const clear = () => { if (timer !== undefined) { window.clearTimeout(timer); timer = undefined; } };
    const tick = async (first = false) => {
      timer = undefined;
      if (stopped || inFlight || (!first && !visible())) return;
      inFlight = true;
      try { await latest.current(); } catch { /* the caller shows its own error */ } finally { inFlight = false; }
      if (!stopped && timer === undefined && visible()) timer = window.setTimeout(() => void tick(), ms);
    };
    const onVisibility = () => { clear(); if (visible()) void tick(); };
    void tick(true);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);
    return () => {
      stopped = true;
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
    };
  }, [enabled, ms]);
}
